import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HeartIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface Nonprofit {
  name: string;
  tagline: string;
  taglineEs?: string;
  url?: string;
  accentColor: string;
  bgColor: string;
  emoji: string;
  logoUrl?: string;
}
const nonprofits: Nonprofit[] = [{
  name: 'Educate 2 Envision',
  tagline: 'Reimagine Rural Education',
  taglineEs: 'Reimaginar la Educación Rural',
  url: 'https://www.educate2envision.org/',
  accentColor: 'text-haa-blue',
  bgColor: 'bg-white',
  emoji: '📚',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/ev4kbKkWLnh6RaghTq6B2v/Screenshot_2026-03-02_at_10.40.07_PM.png'
}, {
  name: 'Partners Of The Americas',
  tagline: 'Connect, Serve, Change Lives',
  taglineEs: 'Conectar, Servir, Cambiar Vidas',
  url: 'https://www.partners.net/',
  accentColor: 'text-haa-skyblue',
  bgColor: 'bg-white',
  emoji: '🎭',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/8FhRxJP7AqSfPUyJJnuVPD/Screenshot_2026-03-02_at_10.27.55_PM.png'
}, {
  name: 'Leadership Mission International',
  tagline: 'Developing leaders for global impact',
  taglineEs: 'Desarrollando líderes para impacto global',
  url: 'https://leadershipmission.org/',
  accentColor: 'text-haa-blue',
  bgColor: 'bg-white',
  emoji: '🌍',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/u4EMTZoVEvHirbk4cRDwqL/Screenshot_2026-03-02_at_1.33.52_AM.png'
}, {
  name: 'Honduras Outreach International',
  tagline: 'Strengthening communities in Honduras',
  taglineEs: 'Fortaleciendo comunidades en Honduras',
  url: 'https://hoi.org/',
  accentColor: 'text-haa-midblue',
  bgColor: 'bg-white',
  emoji: '🤲',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/1Hkzbv9krJHVeCJsoZKFjV/Screenshot_2026-03-02_at_1.35.08_AM.png'
}, {
  name: '1,000 Schools Honduras',
  tagline: 'Education & youth empowerment',
  taglineEs: 'Educación y empoderamiento juvenil',
  url: 'https://onethousandschools.com/',
  accentColor: 'text-sky-500',
  bgColor: 'bg-white',
  emoji: '🏫',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/ctxHhUcNgPAM92U4bemBcp/otc_shh_logo.png'
}, {
  name: 'Young Life Honduras',
  tagline: 'Reaching young people for Christ',
  taglineEs: 'Alcanzando jóvenes para Cristo',
  url: 'https://honduras.younglife.org/',
  accentColor: 'text-lime-600',
  bgColor: 'bg-[#1e2d4a]',
  emoji: '🌱',
  logoUrl: 'https://cdn.magicpatterns.com/uploads/seGgtTTq8VBPFyAiC9dmg7/Screenshot_2026-03-02_at_1.37.01_AM.png'
}];
function NonprofitCard({
  org
}: {
  org: Nonprofit;
}) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <div className={`flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm mx-3 group cursor-default select-none`} style={{
    minWidth: '240px'
  }} data-id="element-2311">
      <div className={`w-10 h-10 rounded-xl ${org.bgColor} flex items-center justify-center flex-shrink-0 text-xl overflow-hidden`} data-id="element-2312">
        {org.logoUrl ? <img src={org.logoUrl} alt={org.name} className="w-8 h-8 object-contain" data-id="element-2313" /> : org.emoji}
      </div>
      <div className="flex flex-col min-w-0" data-id="element-2314">
        <span className={`font-bebas text-base tracking-wider leading-tight ${org.accentColor}`} data-id="element-2315">
          {org.name}
        </span>
        <span className="font-nunito text-xs text-gray-500 leading-tight truncate" data-id="element-2316">
          {isEs && org.taglineEs ? org.taglineEs : org.tagline}
        </span>
      </div>
      {org.url && <a href={org.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" aria-label={`${isEs ? 'Visitar' : 'Visit'} ${org.name}`} data-id="element-2317">
          <ExternalLinkIcon className="w-3.5 h-3.5 text-gray-400 hover:text-haa-blue transition-colors" data-id="element-2318" />
        </a>}
    </div>;
}
export function NonprofitScroller() {
  const titleRef = useRef<HTMLDivElement>(null);
  const inView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  // Duplicate for seamless loop
  const doubled = [...nonprofits, ...nonprofits];
  return <section className="bg-haa-navy/5 py-10 overflow-hidden" aria-label={isEs ? 'Organizaciones destacadas' : 'Featured nonprofits'} data-id="element-2319">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6" data-id="element-2320">
        <motion.div ref={titleRef} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="flex items-center gap-3" data-id="element-2321">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-haa-blue to-haa-midblue flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-2322">
            <HeartIcon className="w-4 h-4 text-white" data-id="element-2323" />
          </div>
          <div data-id="element-2324">
            <span className="font-nunito text-haa-blue text-xs uppercase tracking-[0.3em] font-semibold block leading-none mb-0.5" data-id="element-2325">
              {isEs ? 'Campeones de Honduras' : 'Honduras Champions'}
            </span>
            <h2 className="font-bebas text-2xl text-haa-navy tracking-wider leading-none" data-id="element-2326">
              {isEs ? 'Organizaciones Destacadas Del Mes' : 'Featured Organizations Of The Month'}
            </h2>
          </div>
        </motion.div>
      </div>

      {/* Scroller track */}
      <div className="relative w-full" style={{
      maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)'
    }} data-id="element-2327">
        <div className="flex nonprofit-scroll" style={{
        width: 'max-content'
      }} onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.animationPlayState = 'paused';
      }} onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.animationPlayState = 'running';
      }} data-id="element-2328">
          {doubled.map((org, i) => <NonprofitCard key={`${org.name}-${i}`} org={org} data-id="element-2329" />)}
        </div>
      </div>
    </section>;
}